import { APIError, defaultParseError, fetcher } from "@certn/api";
import {
  Card,
  ContractView,
  downloadPdfFromResponse,
  isPdfEsignDocument,
  SectionMessage,
  Text,
  showToast,
  Watermark,
} from "@certn/cascadia-react";
import { RenderContext } from "@certn/cascadia-react/context/RenderContext";
import { css } from "@emotion/css";
import { useMemo, useContext, useState, useEffect, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";

import { EMPTY_STATE } from "./constants";
import { SummaryOfRightsEsign } from "./SummaryOfRightsEsign";
import { SummaryOfRightsPdf } from "./SummaryOfRightsPdf";
import { APP_IDENTIFIER } from "../../constants";
import { ApplicantDetails } from "../applicant-details/ApplicantDetails";
import { CaseDetails } from "../case-details/CaseDetails";
import { CaseMessages } from "../case-messages/CaseMessages";
import { CheckResultCard } from "../check-result-card/CheckResultCard";
import { PageHeader, FetchPdfOptions } from "../page-header/PageHeader";
import { ReportSummary } from "../report-summary/ReportSummary";
import {
  CheckResult,
  ReportData,
  AppIdentifier,
  AllChecksFulfillmentNotes,
  ReportAttachment,
  CaseReportFilePollResponse,
  CaseReportGenerateResponse,
} from "../types";

const CALIFORNIA_CONSUMER_RIGHTS_CONTRACT_ID = "232150";
const NEW_YORK_ARTICLE_23_A = "510684";
const POLLING_TIME_CUTOFF = 60000; // in ms (allow time for PDF generation + S3 upload)
const POLLING_INTERVAL_DELAY = 1500; // in ms
const EMPTY_ATTACHMENTS: ReportAttachment[] = [];

type ReportProps = {
  reportData: ReportData;
  clickwrapAccessID: string;
  appIdentifier: AppIdentifier;
  responsive?: boolean;
  watermark?: string;
  fulfillmentNotes?: AllChecksFulfillmentNotes;
  isLoading?: boolean;
};

export function Report({
  reportData,
  clickwrapAccessID,
  appIdentifier,
  responsive,
  watermark,
  fulfillmentNotes: allChecksFulfillmentNotes,
  isLoading,
}: ReportProps) {
  const { t } = useTranslation();
  const isPDF = useContext(RenderContext).isForPdfGeneration;
  const [hasCaliforniaAddress, setHasCaliforniaAddress] = useState<boolean>();
  const [hasNYCArticleReq, setHasNYCArticleReq] = useState<boolean>();
  const intervalFetch = useRef<number | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const {
    case_details: caseDetails,
    applicant_info: applicantDetails,
    date_completed: dateCompleted,
    date_generated: dateGenerated,
    checks: checkResults,
  } = reportData ?? EMPTY_STATE;
  const attachments = reportData?.report_attachments ?? EMPTY_ATTACHMENTS;
  const currentAddress = reportData?.applicant_info?.current_location;
  const needsNYCNotice = reportData?.case_details?.needs_nyc_article_23a_notice;
  const pactSafeAttachments = useMemo(
    () =>
      attachments.filter((attachment) => {
        const hasSuperdoc =
          Boolean(attachment.pdf_url) || Boolean(attachment.esign?.url);
        return !hasSuperdoc && Boolean(attachment.external_id);
      }),
    [attachments]
  );
  const regulatoryContractIDs = useMemo(() => {
    const ids: string[] = [];

    if (currentAddress?.country === "US") {
      if (currentAddress?.administrative_division_1 === "CA") {
        ids.push(CALIFORNIA_CONSUMER_RIGHTS_CONTRACT_ID);
      }
    }

    if (needsNYCNotice) {
      ids.push(NEW_YORK_ARTICLE_23_A);
    }

    return ids;
  }, [currentAddress, needsNYCNotice]);

  useEffect(() => {
    setHasCaliforniaAddress(
      currentAddress?.country === "US" &&
        currentAddress?.administrative_division_1 === "CA"
    );
    setHasNYCArticleReq(Boolean(needsNYCNotice));
  }, [currentAddress, needsNYCNotice]);

  useEffect(() => {
    // clear interval timer on component unmount
    return () => {
      if (intervalFetch.current) {
        clearInterval(intervalFetch.current);
      }
    };
  }, [intervalFetch]);

  function getGenerateUrl(
    appIdentifier: APP_IDENTIFIER,
    case_short_id: string
  ): string {
    let apiUrl = `applicant/case/${case_short_id}/generate-report/`;
    if (appIdentifier === APP_IDENTIFIER.CLIENT_PORTAL) {
      apiUrl = `client-portal/case/${case_short_id}/generate-report/`;
    } else if (appIdentifier === APP_IDENTIFIER.CASE_MANAGEMENT) {
      apiUrl = `case-management/cases/${case_short_id}/generate-report/`;
    }
    return apiUrl;
  }

  function getPollUrl(appIdentifier: APP_IDENTIFIER, file_id: string): string {
    let apiUrl = `applicant/case/report-files/${file_id}/`;
    if (appIdentifier === APP_IDENTIFIER.CLIENT_PORTAL) {
      apiUrl = `client-portal/case/report-files/${file_id}/`;
    } else if (appIdentifier === APP_IDENTIFIER.CASE_MANAGEMENT) {
      apiUrl = `case-management/cases/report-files/${file_id}/`;
    }
    return apiUrl;
  }

  function getDownloadUrl(
    appIdentifier: APP_IDENTIFIER,
    file_id: string
  ): string {
    return `${getPollUrl(appIdentifier, file_id)}download/`;
  }

  async function waitForPDFToGenerate(
    appIdentifier: APP_IDENTIFIER,
    caseReportFileId: string
  ): Promise<CaseReportFilePollResponse> {
    const apiPollURL = getPollUrl(appIdentifier, caseReportFileId);
    if (intervalFetch.current) {
      clearInterval(intervalFetch.current);
    }

    return new Promise((resolve, reject) => {
      let pollingAttempts = 0;
      intervalFetch.current = setInterval(async () => {
        let response: CaseReportFilePollResponse;
        try {
          response = await fetcher.request<CaseReportFilePollResponse>(
            `/api/internal/${apiPollURL}`
          );
        } catch {
          if (intervalFetch.current !== null) {
            clearInterval(intervalFetch.current);
          }
          reject(t("Unable to download report."));
          return;
        }

        if (response.status === "COMPLETE") {
          resolve(response);
        }

        pollingAttempts += 1;
        if (
          response.status === "FAILED" ||
          pollingAttempts * POLLING_INTERVAL_DELAY >= POLLING_TIME_CUTOFF
        ) {
          if (intervalFetch.current !== null) {
            clearInterval(intervalFetch.current);
          }
          reject(t("Unable to download report."));
        }

        if (response.status !== "PENDING") {
          if (intervalFetch.current !== null) {
            clearInterval(intervalFetch.current);
          }
        }
      }, POLLING_INTERVAL_DELAY);
    });
  }

  async function fetchPDF(options?: FetchPdfOptions) {
    setIsDownloading(true);

    const apiGenerateUrl = getGenerateUrl(
      appIdentifier as APP_IDENTIFIER,
      caseDetails.short_id
    );

    try {
      const generateUrl = `/api/internal/${apiGenerateUrl}`;
      const body: { check_ids?: string[]; exclude_overview?: boolean } = {};
      if (options?.checkIds?.length) {
        // Deliberately signal that we only want a subset of the checks; omitting this indicates we want all checks.
        body.check_ids = options.checkIds;
      }
      if (options?.excludeOverview) {
        body.exclude_overview = true;
      }
      const response: CaseReportGenerateResponse = Object.keys(body).length
        ? await fetcher.submit<CaseReportGenerateResponse>(generateUrl, body)
        : await fetcher.submit<CaseReportGenerateResponse>(generateUrl);

      const pollResult = await waitForPDFToGenerate(
        appIdentifier as APP_IDENTIFIER,
        response.case_report_file_id
      );

      // The backend owns the authenticated-case-report-pdf-download flag: it only
      // omits pdf_url when the authenticated download endpoint is enabled.
      if (pollResult.pdf_url) {
        window.location.assign(pollResult.pdf_url);
      } else {
        const downloadPath = `/api/internal/${getDownloadUrl(
          appIdentifier as APP_IDENTIFIER,
          response.case_report_file_id
        )}`;
        const pdfBlob = await fetcher.requestBlob(downloadPath, {
          method: "GET",
          headers: { Accept: "application/pdf" },
        });
        await downloadPdfFromResponse(
          new Response(pdfBlob, { status: 200, statusText: "OK" }),
          pdfTitleName || "report"
        );
      }
    } catch (error) {
      const message =
        error instanceof APIError
          ? defaultParseError(error) ?? t("Unable to download report.")
          : t("Unable to download report.");
      showToast(message, {
        type: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  }

  const pdfTitleName = applicantDetails?.current_name?.given_name
    ? `${applicantDetails.current_name.given_name} ${
        applicantDetails.current_name.family_name || ""
      }`.trim()
    : applicantDetails?.email;

  const shouldShowOverview = !isPDF || !reportData?.exclude_overview;
  const showAttachmentsOnSurface =
    isPDF || appIdentifier === APP_IDENTIFIER.APPLICANT_PORTAL;
  const showSuperdocOnWeb =
    !isPDF && appIdentifier === APP_IDENTIFIER.APPLICANT_PORTAL;
  const superdocAttachments = attachments.filter(
    (attachment) =>
      Boolean(attachment.pdf_url) || Boolean(attachment.esign?.url)
  );
  const showPactSafeAttachments =
    pactSafeAttachments.length > 0 && showAttachmentsOnSurface;
  const showPactSafeRegulatoryNotices =
    regulatoryContractIDs.length > 0 && showAttachmentsOnSurface;

  return (
    <div className={styles.container}>
      <Card
        className={isPDF ? undefined : styles.cardContainer}
        type="large"
        responsive={responsive}
        testID="report-card"
        border={isPDF ? "none" : "border-base"}
      >
        {shouldShowOverview && (
          <>
            <div style={{ marginBottom: "var(--space-4)" }}>
              {hasCaliforniaAddress &&
                (isPDF ||
                  appIdentifier === APP_IDENTIFIER.APPLICANT_PORTAL) && (
                  <div
                    style={{
                      marginBottom: "var(--space-4)",
                    }}
                  >
                    <SectionMessage
                      type="default"
                      icon="info"
                      sectionTitle=""
                      testID="california-applicant-disclaimer"
                    >
                      <Text
                        className={
                          styles.californiaCivilCodeNoticeLegalStyleRequirements
                        }
                      >
                        <Trans>
                          This report does not guarantee the accuracy or
                          truthfulness of the information as to the subject of
                          the investigation, but only that it is accurately
                          copied from public records, and information generated
                          as a result of identity theft, including evidence of
                          criminal activity, may be inaccurately associated with
                          the consumer who is the subject of the report.
                          <br />
                          <br />
                          An investigative consumer reporting agency shall
                          provide a consumer seeking to obtain a copy of a
                          report or making a request to review a file, a written
                          notice in simple, plain English and Spanish setting
                          forth the terms and conditions of his or her right to
                          receive all disclosures, as provided in Section
                          1786.26 of the California Civil Code.
                        </Trans>
                      </Text>
                    </SectionMessage>
                  </div>
                )}
              {hasNYCArticleReq &&
                (isPDF ||
                  appIdentifier === APP_IDENTIFIER.APPLICANT_PORTAL) && (
                  <div
                    style={{
                      marginBottom: "var(--space-4)",
                    }}
                  >
                    <SectionMessage
                      type="default"
                      icon="info"
                      sectionTitle=""
                      testID="nyc-article-23a-notice"
                    >
                      <Text
                        className={
                          styles.californiaCivilCodeNoticeLegalStyleRequirements
                        }
                      >
                        <Trans>
                          In accordance with New York law, you have the right to
                          correct any incorrect information in your report.
                        </Trans>
                      </Text>
                    </SectionMessage>
                  </div>
                )}
              <PageHeader
                name={pdfTitleName}
                isPDF={isPDF}
                fetchPDF={fetchPDF}
                isDownloading={isDownloading}
                isLoading={isLoading}
                checks={checkResults}
                appIdentifier={appIdentifier}
              />
              <Watermark watermark={watermark}>
                <CaseMessages
                  cancellationReason={caseDetails?.cancellation_reason}
                  checks={checkResults}
                />
                <ApplicantDetails
                  firstName={applicantDetails?.current_name?.given_name}
                  lastName={applicantDetails?.current_name?.family_name}
                  middleName={applicantDetails?.current_name?.additional_name}
                  email={applicantDetails?.email}
                  currentLocation={applicantDetails?.current_location}
                  dateOfBirth={applicantDetails?.date_of_birth}
                  isLoading={isLoading}
                />
                <CaseDetails
                  caseID={caseDetails?.short_id}
                  dateOrdered={caseDetails?.date_ordered}
                  dateGenerated={dateGenerated}
                  reportCompleted={dateCompleted}
                  isPDF={isPDF}
                  isLoading={isLoading}
                />
              </Watermark>
              {isPDF && <ReportSummary checkResults={checkResults} />}
            </div>
            {isPDF && (
              <Trans>
                <Text
                  style="form/body-primary"
                  testID="report-disclaimer"
                  aria-label="Report results disclaimer"
                >
                  Report results shall in no way be construed as Certn's
                  opinion, recommendation, or forecast on the solvency,
                  creditworthiness, capability, intention, integrity or motives
                  of the subject of the report. The report results merely
                  reflect a recording of information received by Certn from
                  various sources and does not constitute legal or other
                  professional advice. Certn accepts no liability for any
                  damages or losses arising from the opinions, comments, actions
                  or decisions made by the recipient of the report results. The
                  application of the report results requires independent
                  judgment and skill.
                </Text>
              </Trans>
            )}
          </>
        )}
        {/* When overview is omitted, the first CheckResultCard is :first-child of
            this Card and print CSS skips page-break-before (no blank page 1).
            Do not insert nodes between the overview block and the check list. */}
        {checkResults?.map((checkResult: CheckResult) => (
          <CheckResultCard
            checkResult={checkResult}
            isPDF={isPDF}
            key={checkResult.id}
            allCheckResults={checkResults}
            applicantDetails={applicantDetails}
            allChecksFulfillmentNotes={allChecksFulfillmentNotes || []}
          />
        ))}
        {!isPDF && (
          <Trans>
            <Text
              style="form/body-primary"
              testID="report-disclaimer"
              aria-label="Report results disclaimer"
            >
              Report results shall in no way be construed as Certn's opinion,
              recommendation, or forecast on the solvency, creditworthiness,
              capability, intention, integrity or motives of the subject of the
              report. The report results merely reflect a recording of
              information received by Certn from various sources and does not
              constitute legal or other professional advice. Certn accepts no
              liability for any damages or losses arising from the opinions,
              comments, actions or decisions made by the recipient of the report
              results. The application of the report results requires
              independent judgment and skill.
            </Text>
          </Trans>
        )}
      </Card>
      {shouldShowOverview &&
        showSuperdocOnWeb &&
        superdocAttachments.map((attachment) => {
          const esignIsPdf = isPdfEsignDocument(attachment.esign);
          const canRenderEsignHtml =
            Boolean(attachment.esign?.url) && !esignIsPdf;
          const pdfUrl =
            attachment.pdf_url ||
            (esignIsPdf ? attachment.esign?.url : undefined);
          return (
            <Card
              key={attachment.template_id}
              className={styles.summaryOfRightsCard}
            >
              <Text
                testID="summary-of-rights-title"
                style="global/heading-large"
              >
                {attachment.template_name}
              </Text>
              {canRenderEsignHtml && attachment.esign && (
                <SummaryOfRightsEsign data={attachment.esign} />
              )}
              {pdfUrl && !canRenderEsignHtml && (
                <SummaryOfRightsPdf
                  url={pdfUrl}
                  title={attachment.template_name}
                />
              )}
            </Card>
          );
        })}
      {shouldShowOverview &&
        showPactSafeAttachments &&
        pactSafeAttachments.map((attachment) => {
          const externalId = attachment.external_id;
          if (!externalId) {
            return null;
          }

          return (
            <PactSafeAttachmentCard
              key={attachment.template_id}
              accessID={clickwrapAccessID}
              externalId={externalId}
              templateName={attachment.template_name}
            />
          );
        })}
      {shouldShowOverview && showPactSafeRegulatoryNotices && (
        <Card className={styles.summaryOfRightsCard}>
          <Text testID="summary-of-rights-title" style="global/heading-large">
            {t("Summary of Rights")}
          </Text>
          <ContractView
            accessID={clickwrapAccessID}
            contractIDs={regulatoryContractIDs}
          />
        </Card>
      )}
    </div>
  );
}

type PactSafeAttachmentCardProps = {
  accessID: string;
  externalId: string;
  templateName: string;
};

function PactSafeAttachmentCard({
  accessID,
  externalId,
  templateName,
}: PactSafeAttachmentCardProps) {
  const contractIDs = useMemo(() => [externalId], [externalId]);

  return (
    <Card className={styles.summaryOfRightsCard}>
      <Text testID="summary-of-rights-title" style="global/heading-large">
        {templateName}
      </Text>
      <ContractView accessID={accessID} contractIDs={contractIDs} />
    </Card>
  );
}

const styles = {
  container: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  cardContainer: css({
    padding: `${"var(--space-5)"} ${"var(--space-4)"}`,
  }),
  summaryOfRightsCard: css({
    padding: `${"var(--space-5)"} ${"var(--space-4)"}`,
    // PactSafe HTML grew the card; don't clip SuperDoc once it sizes to content.
    overflow: "visible !important",
  }),
  californiaCivilCodeNoticeLegalStyleRequirements: css({
    fontSize: "14pt",
    fontWeight: "bold",
  }),
};
